import { Button, Grid } from '@material-ui/core';
import {
  Children,
  cloneElement,
  isValidElement,
  PropsWithChildren,
  ReactNode,
} from 'react';
import {
  EntityApiDefinitionCard,
  EntityConsumedApisCard,
  EntityConsumingComponentsCard,
  EntityHasApisCard,
  EntityProvidedApisCard,
  EntityProvidingComponentsCard,
} from '@backstage/plugin-api-docs';
import {
  EntityAboutCard,
  EntityDependsOnComponentsCard,
  EntityDependsOnResourcesCard,
  EntityHasComponentsCard,
  EntityHasResourcesCard,
  EntityHasSystemsCard,
  EntityLayout,
  EntityLinksCard,
  EntitySwitch,
  EntityOrphanWarning,
  EntityProcessingErrorsPanel,
  isKind,
  hasCatalogProcessingErrors,
  isOrphan,
  hasRelationWarnings,
  EntityRelationWarning,
  isComponentType,
} from '@backstage/plugin-catalog';
import { entityRouteRef, useEntity } from '@backstage/plugin-catalog-react';
import { useRouteRef } from '@backstage/core-plugin-api';
import {
  isGithubActionsAvailable,
  EntityGithubActionsContent,
} from '@backstage-community/plugin-github-actions';
import {
  EntityUserProfileCard,
  EntityGroupProfileCard,
  EntityMembersListCard,
  EntityOwnershipCard,
} from '@backstage/plugin-org';
import { EntityTechdocsContent } from '@backstage/plugin-techdocs';
import { CodeSnippet, EmptyState } from '@backstage/core-components';
import {
  Direction,
  EntityCatalogGraphCard,
} from '@backstage/plugin-catalog-graph';
import {
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { EntityTodoContent } from '@backstage-community/plugin-todo';
import { EntityEndOfLifeCard } from '@dweber019/backstage-plugin-endoflife';

import { EntitySecurityInsightsContent } from '@roadiehq/backstage-plugin-security-insights';

import { EmisxReleasesPage } from '@internal/backstage-plugin-emisx-releases';

import { DoraScorecard } from '@jikwan/backstage-plugin-dora-scorecard';

import {
  EntityObservabilityContent,
  isDynatraceAvailable,
} from '@internal/backstage-plugin-emis-observability';
import {
  ApiComplianceContent,
  Scorecard,
} from '@internal/backstage-plugin-emis-tech-insights';
import {
  AppExtensionManifestContent,
  entityToManifest,
} from '@internal/backstage-plugin-app-extensions';

const addAbsoluteTabLinks = (
  children: ReactNode,
  entityUrl: string,
): ReactNode =>
  Children.map(children, child => {
    if (!isValidElement(child)) {
      return child;
    }

    const childProps = child.props as {
      path?: string;
      tabProps?: Record<string, unknown>;
      children?: ReactNode;
    };
    const nestedChildren: ReactNode = childProps.children
      ? addAbsoluteTabLinks(childProps.children, entityUrl)
      : childProps.children;

    if (childProps.path) {
      const tabPath = childProps.path.replace(/^\/+/, '');
      const to = tabPath ? `${entityUrl}/${tabPath}` : entityUrl;
      return cloneElement(child, {
        tabProps: { ...childProps.tabProps, to },
        children: nestedChildren,
      });
    }

    return nestedChildren !== childProps.children
      ? cloneElement(child, { children: nestedChildren })
      : child;
  });

const AbsoluteEntityLayout = ({ children }: PropsWithChildren) => {
  const { entity } = useEntity();
  const entityRoute = useRouteRef(entityRouteRef);
  const entityUrl = entityRoute({
    kind: entity.kind.toLowerCase(),
    namespace: (entity.metadata.namespace || 'default').toLowerCase(),
    name: entity.metadata.name.toLowerCase(),
  });

  return (
    <EntityLayout>{addAbsoluteTabLinks(children, entityUrl)}</EntityLayout>
  );
};

const getAnnotation = (annotationExample: string) => {
  return `metadata:
  name: example
  annotations:
    ${annotationExample}`;
};

const getEmptyState = (
  plugin: string,
  link: string,
  missingAnnotationExample: string,
) => (
  <EmptyState
    title={plugin}
    missing="field"
    description={`You need to add an annotation to your component if you want to enable ${plugin} integration.`}
    action={
      <>
        <CodeSnippet
          text={getAnnotation(missingAnnotationExample)}
          language="yaml"
          showLineNumbers
          highlightedNumbers={[3, 4, 5, 6]}
        />
        <Button variant="contained" color="primary" href={link}>
          Read more
        </Button>
      </>
    }
  />
);

const techdocsContent = (
  <EntityTechdocsContent>
    <TechDocsAddons>
      <ReportIssue />
    </TechDocsAddons>
  </EntityTechdocsContent>
);

const cicdContent = (
  <>
    <EntitySwitch>
      <EntitySwitch.Case if={isComponentType(['service', 'website'])}>
        <Grid container spacing={3} alignItems="stretch">
          <Grid item md={12}>
            <DoraScorecard />
          </Grid>
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={isGithubActionsAvailable}>
        <EntityGithubActionsContent />
      </EntitySwitch.Case>

      <EntitySwitch.Case>
        {getEmptyState(
          'GitHub Actions',
          'https://backstage.io/docs/features/software-catalog/well-known-annotations',
          'github.com/project-slug: example/example',
        )}
      </EntitySwitch.Case>
    </EntitySwitch>
  </>
);

const entityWarningContent = (
  <>
    <EntitySwitch>
      <EntitySwitch.Case if={isOrphan}>
        <Grid item xs={12}>
          <EntityOrphanWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasRelationWarnings}>
        <Grid item xs={12}>
          <EntityRelationWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasCatalogProcessingErrors}>
        <Grid item xs={12}>
          <EntityProcessingErrorsPanel />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
  </>
);

const OverviewContent = () => {
  return (
    <Grid container spacing={3} alignItems="stretch">
      {entityWarningContent}
      <Grid item md={6}>
        <Grid container spacing={3} alignItems="stretch">
          <Grid item md={12}>
            <EntityAboutCard />
          </Grid>
        </Grid>
      </Grid>

      <Grid item md={6}>
        <EntityLinksCard />
      </Grid>

      <Grid item md={12}>
        <EntityCatalogGraphCard height={300} />
      </Grid>
    </Grid>
  );
};

const ApiOverviewContent = () => {
  return (
    <Grid container spacing={3}>
      {entityWarningContent}
      <Grid item md={6}>
        <EntityAboutCard />
      </Grid>
      <Grid item md={6}>
        <EntityLinksCard />
      </Grid>
      <Grid container item md={12}>
        <Grid item md={12}>
          <EntityProvidingComponentsCard />
        </Grid>
        <Grid item md={12}>
          <EntityConsumingComponentsCard />
        </Grid>
      </Grid>
      <Grid item md={12}>
        <EntityCatalogGraphCard height={200} />
      </Grid>
    </Grid>
  );
};

const AppExtensionContent = () => {
  const { entity } = useEntity();
  const manifest = entityToManifest(entity);

  return manifest ? (
    <AppExtensionManifestContent manifest={manifest} />
  ) : (
    <EmptyState
      title="Manifest unavailable"
      missing="field"
      description="This entity does not contain a valid app extension manifest."
    />
  );
};

const observabilityRoute = (
  <EntityLayout.Route
    if={isDynatraceAvailable}
    path="/observability"
    title="Observability"
  >
    <EntityObservabilityContent />
  </EntityLayout.Route>
);

const componentPage = (
  <AbsoluteEntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <OverviewContent />
    </EntityLayout.Route>
    {observabilityRoute}

    <EntityLayout.Route
      if={isKind('appextension')}
      path="/app-extensions"
      title="App Extensions"
    >
      <AppExtensionContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/tech-insights" title="Scorecards">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6} xs={12}>
          <Scorecard
            title="Onboarding"
            description="Is your component fully onboarded into Developer Hub?"
            factRetrieverName="onboarding"
            expanded={false}
          />
        </Grid>
        <EntitySwitch>
          <EntitySwitch.Case if={isComponentType(['service', 'website'])}>
            <Grid item md={6} xs={12}>
              <Scorecard
                title="Operational"
                description="Is your component operationally ready?"
                factRetrieverName="operational"
                expanded={false}
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <Scorecard
                title="Engineering"
                description="Is your component following engineering standards?"
                factRetrieverName="engineering"
                expanded={false}
              />
            </Grid>
          </EntitySwitch.Case>
        </EntitySwitch>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route if={isComponentType('service')} path="/api" title="API">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={12}>
          <EntityProvidedApisCard />
        </Grid>
        <Grid item md={12}>
          <EntityConsumedApisCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/ci-cd" title="CI/CD">
      {cicdContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/security-insights" title="Security Insights">
      <EntitySecurityInsightsContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/todo" title="Todo">
      <EntityTodoContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={12}>
          <EntityDependsOnComponentsCard />
        </Grid>
        <Grid item md={12}>
          <EntityDependsOnResourcesCard />
        </Grid>
        <Grid item md={12}>
          <EntityEndOfLifeCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/releases" title="Releases">
      <EmisxReleasesPage />
    </EntityLayout.Route>
  </AbsoluteEntityLayout>
);

const apiPage = (
  <AbsoluteEntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <ApiOverviewContent />
    </EntityLayout.Route>
    {observabilityRoute}

    <EntityLayout.Route path="/tech-insights" title="Scorecards">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6} xs={12}>
          <Scorecard
            title="Onboarding"
            description="Ensure that your component is properly onboarded into Developer Hub"
            factRetrieverName="onboarding"
          />
        </Grid>
        <Grid item md={6} xs={12}>
          <Scorecard
            title="API Compliance"
            description="Is your API specification secure and compliant?"
            factRetrieverName="api-compliance"
          />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/definition" title="Definition">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityApiDefinitionCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/analysis" title="Analysis">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <ApiComplianceContent />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </AbsoluteEntityLayout>
);

const userPage = (
  <AbsoluteEntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityUserProfileCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    {observabilityRoute}
  </AbsoluteEntityLayout>
);

const groupPage = (
  <AbsoluteEntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityGroupProfileCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityMembersListCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityLinksCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    {observabilityRoute}
  </AbsoluteEntityLayout>
);

const systemPage = (
  <AbsoluteEntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={400} />
        </Grid>
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid item md={8}>
          <EntityHasComponentsCard />
        </Grid>
        <Grid item md={6}>
          <EntityHasApisCard />
        </Grid>
        <Grid item md={6}>
          <EntityHasResourcesCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    {observabilityRoute}
    <EntityLayout.Route path="/diagram" title="Diagram">
      <EntityCatalogGraphCard
        direction={Direction.TOP_BOTTOM}
        title="System Diagram"
        height={700}
        relations={[
          RELATION_PART_OF,
          RELATION_HAS_PART,
          RELATION_API_CONSUMED_BY,
          RELATION_API_PROVIDED_BY,
          RELATION_CONSUMES_API,
          RELATION_PROVIDES_API,
          RELATION_DEPENDENCY_OF,
          RELATION_DEPENDS_ON,
        ]}
        unidirectional={false}
      />
    </EntityLayout.Route>
  </AbsoluteEntityLayout>
);

const domainPage = (
  <AbsoluteEntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={400} />
        </Grid>
        <Grid item md={6}>
          <EntityHasSystemsCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    {observabilityRoute}
  </AbsoluteEntityLayout>
);

const resourcePage = (
  <AbsoluteEntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <EntityAboutCard />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={400} />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    {observabilityRoute}
  </AbsoluteEntityLayout>
);

export const entityPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isKind('component')} children={componentPage} />
    <EntitySwitch.Case if={isKind('api')} children={apiPage} />
    <EntitySwitch.Case if={isKind('group')} children={groupPage} />
    <EntitySwitch.Case if={isKind('user')} children={userPage} />
    <EntitySwitch.Case if={isKind('system')} children={systemPage} />
    <EntitySwitch.Case if={isKind('domain')} children={domainPage} />
    <EntitySwitch.Case if={isKind('resource')} children={resourcePage} />

    <EntitySwitch.Case>{componentPage}</EntitySwitch.Case>
  </EntitySwitch>
);
